import { DataTable } from '@/global/components/DataGrid/components/DataTable'
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '@/global/components/ui/breadcrumb'
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '@/global/components/ui/tabs'
import { usePageTitle } from '@/hooks/usePageTitle'
import { cn } from '@/lib/utils'
import { onlyDefined } from '@acme/common'
import React, { HTMLProps, ReactNode, memo } from 'react'
import FiltrosPadrao from './Filtros'

interface SimpleContainerProps {
    children: ReactNode
    className?: string
}

/** div estilizado
 *
 * @example
 * <ContentGuide>
 *   <ContentGuide.Card>
 *     <ContentGuide.Header label={['Home', { title: 'Section', to: '/section' }, 'Current Page']} />
 *     <ContentGuide.DataGrid {...props} />
 *   </ContentGuide.Card>
 * </ContentGuide>
 */
export function ContentGuide({ children, ...props }: SimpleContainerProps) {
    return (
        <div
            className={cn(
                'flex flex-col items-center justify-center',
                'overflow-hidden',
                'h-[calc(100dvh_-_4rem)]',
                'p-0 pt-0.5 sm:py-3 sm:px-4',
                'dark:bg-popover',
                props.className,
            )}
            {...props}
        >
            {children}
        </div>
    )
}

function CardGuide({ children, className }: SimpleContainerProps) {
    return (
        <div
            className={cn(
                'size-full 2xl:container flex gap-1',
                'flex-col justify-center items-center',
                'overflow-hidden p-3 sm:rounded-sm rounded-none',
                'rounded-lg shadow-lg',
                'bg-card border border-border dark:border-0',
                'max-2xl:border-0 max-2xl:rounded-none',
                className,
            )}
        >
            {children}
        </div>
    )
}

interface TabbedGuideTab {
    value: string
    label: ReactNode
    content: ReactNode
    disabled?: boolean
}

interface TabbedGuideProps {
    tabs: TabbedGuideTab[]
    value?: string
    defaultValue?: string
    onValueChange?: (value: string) => void
    className?: string
}

function TabbedGuide({ tabs, className, ...props }: TabbedGuideProps) {
    return (
        <Tabs
            defaultValue={tabs.at(0)?.value}
            {...props}
            className={cn(
                'size-full mx-auto max-w-[1500px]',
                'gap-2 lg:gap-4 p-2 sm:p-3 lg:p-4',
                'overflow-hidden rounded-none sm:rounded-lg shadow-lg',
                'bg-card dark:bg-card border border-border dark:border-0',
                className,
            )}
        >
            <TabsList
                className="shrink-0 min-w-full sm:w-fit border-b pb-1"
                variant="underline"
            >
                {tabs.map(tab => (
                    <TabsTrigger
                        key={tab.value}
                        value={tab.value}
                        disabled={tab.disabled}
                        className="text-lg"
                    >
                        {tab.label}
                    </TabsTrigger>
                ))}
            </TabsList>
            <div className="flex-1 px-2 overflow-hidden">
                {tabs.map(tab => (
                    <TabsContent
                        key={tab.value}
                        value={tab.value}
                        className="min-h-0 h-full p-0 overflow-y-auto overflow-x-hidden"
                    >
                        {tab.content}
                    </TabsContent>
                ))}
            </div>
        </Tabs>
    )
}

type Label = (string | { title: string; to: string } | React.ReactNode)[]

interface HeaderGuideProps extends Omit<HTMLProps<HTMLDivElement>, 'label'> {
    label: Label
}

function HeaderGuide({ children, label, ...props }: HeaderGuideProps) {
    const pageTitle = (() => {
        const titles = label
            .filter(onlyDefined)
            .filter(it => typeof it === 'string')
        let title = titles.at(1)
        title ??= titles.at(0)
        return title as string | undefined
    })()

    usePageTitle(pageTitle)

    return (
        <div
            className={cn(
                'w-full flex flex-wrap justify-between items-center gap-x-2 gap-y-1',
                'py-1 lg:flex-nowrap lg:h-16 lg:py-0',
                'overflow-x-auto overflow-y-clip scrollbar-minimal',
                props.className,
            )}
        >
            <Breadcrumb className="shrink-0">
                <BreadcrumbList className="flex-nowrap">
                    {label.filter(onlyDefined).map((item, index, arr) => {
                        const isLast = index === arr.length - 1,
                            separator = !isLast ? (
                                <BreadcrumbSeparator key={index + 's'} />
                            ) : null,
                            Comp = isLast ? BreadcrumbPage : BreadcrumbItem

                        if (!item) return null
                        else if (typeof item === 'string')
                            return [
                                <Comp key={index}>
                                    <span
                                        className={cn(
                                            'whitespace-nowrap hidden xl:block!',
                                            isLast ? 'block!' : '',
                                        )}
                                    >
                                        {item}
                                    </span>
                                    {!isLast && (
                                        <span className="block xl:hidden">
                                            ...
                                        </span>
                                    )}
                                </Comp>,
                                separator,
                            ]
                        else if (
                            item instanceof Object &&
                            'title' in item &&
                            'to' in item
                        )
                            return [
                                <Comp key={index}>
                                    <BreadcrumbLink href={item.to}>
                                        {item.title}
                                    </BreadcrumbLink>
                                </Comp>,
                                separator,
                            ]
                        else
                            return [
                                <Comp key={index}>
                                    <span>{item}</span>
                                </Comp>,
                                separator,
                            ]
                    })}
                </BreadcrumbList>
            </Breadcrumb>
            <div
                className="ml-auto flex min-w-0 max-w-full items-center gap-2"
                {...props}
            >
                {children}
            </div>
        </div>
    )
}

/** div estilizado
 * @example
 * <ContentGuide.Card>
 *   <p>Conteúdo do card</p>
 * </ContentGuide.Card>
 */
ContentGuide.Card = memo(CardGuide)
/** div com breadcrumbs
 *
 * @example
 * <ContentGuide.Header label={['Home', { title: 'Section', to: '/section' }, 'Current Page']} />
 */
ContentGuide.Header = memo(HeaderGuide)
/** card com abas no topo, mesma largura e espaçamento em toda tela com abas
 *
 * @example
 * <ContentGuide>
 *   <ContentGuide.Tabbed tabs={[{ value: 'dados', label: 'Dados', content: <Form /> }]} />
 * </ContentGuide>
 */
ContentGuide.Tabbed = TabbedGuide

ContentGuide.DataGrid = DataTable
ContentGuide.Filtros = memo(FiltrosPadrao)

export default ContentGuide
